// QuizGame.js

import React, { useEffect, useState } from "react"
import QuizOverview from "./QuizOverview"
import { replaceEmojis, traverseAndReplaceEmojis } from "./component/emoji/emoji";
import styled, { keyframes } from "styled-components";

// Definieer de spinner animatie en styled component
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 4px solid #f2eff0;
  border-top: 4px solid ${(props) => props.themeColor || "#c1c1c6"};
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: ${spin} 1.5s linear infinite;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// Utility function to shuffle an array using Fisher-Yates algorithm
const shuffleArray = (array) => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// Mapping function to handle quiz_item_id inconsistencies
const mapQuizItemId = (id) => {
  // Replace spaces with underscores and convert to lowercase for consistency
  return id.replace(/\s+/g, "_").toLowerCase()
}

const QuizGame = () => {
  const [QuizId, setQuizId] = useState("")
  const [QuestionIds, setQuestionIds] = useState([])
  const [ResultIds, setResultIds] = useState([])
  const [ShapeIds, setShapeIds] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [quiz, setQuiz] = useState(null)
  const [oriQuiz, setOriQuiz] = useState(null)

  let baseUrlContent = ""
  let gameUrl = ""

  if (typeof window !== "undefined") {
    baseUrlContent = window.location.href.includes("localhost")
      ? "http://localhost:1337"
      // ? "https://dudwpkgwkyw9w.cloudfront.net"
      : "https://dudwpkgwkyw9w.cloudfront.net"
    gameUrl = window.location.href.includes("localhost")
      ? "http://localhost:8000/"
      : "https://tools.vrtinnovatie.be/EDUbox/games/"
  }

  // Extract QuizId from URL
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      setQuizId(params.get("quiz") || "")
    }
  }, [])

  // Extract QuestionIds from URL
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      setQuestionIds(params.getAll("question"))
    }
  }, [])

  // Extract ResultIds from URL
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      setResultIds(params.getAll("result"))
    }
  }, [])

  // Extract ShapeIds from URL
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      setShapeIds(params.getAll("shape"))
    }
  }, [])

  // Fetch Quiz Data when QuizId changes
  useEffect(() => {
    if (QuizId && typeof window !== "undefined") {
      fetchQuizes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [QuizId])

  const fetchQuizes = () => {
  const apiUrl = `${baseUrlContent}/api/quiz-games/${QuizId}`
  const myHeaders = new Headers()

  const requestOptions = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  }

  fetch(apiUrl, requestOptions)
    .then(response => {
      if (!response.ok) {
        throw new Error("Data cannot be retrieved, database is not responding.")
      }
      return response.json()
    })
    .then(resultStep => {
      const toolData = traverseAndReplaceEmojis(resultStep)
      return toolData
    })
    .then(result => {
      const toolData = result.data.attributes.toolData
      setOriQuiz(toolData)

      let filteredQuizes = []
      let filteredResults = []
      let filteredShapes = []

      // Synthetic StartScreen item — wordt gebruikt als 'question=startscreen_name'
      const startScreenItem = {
        __component: "quiz-game-types.start-screen",
        quiz_item_id: "startscreen_name",
      }

      // 1) Shapes filteren + sorteren (ongewijzigd op basis van URL)
      if (ShapeIds.length > 0) {
        filteredShapes = toolData.quiz_shape
          .filter(shape => ShapeIds.includes(shape.quiz_shape_id))
          .sort(
            (a, b) =>
              ShapeIds.indexOf(a.quiz_shape_id) - ShapeIds.indexOf(b.quiz_shape_id)
          )
      } else {
        filteredShapes = toolData.quiz_shape
      }

      // 2) Quizes volgens URL-volgorde
      if (QuestionIds.length > 0) {
        // Bouw in exact dezelfde volgorde als in de URL
        filteredQuizes = QuestionIds.reduce((acc, id) => {
          const mapped = mapQuizItemId(id)
          if (mapped === "startscreen_name") {
            // voeg de synthetische StartScreen toe
            acc.push(startScreenItem)
            return acc
          }
          // zoek normale quiz-items
          const q =
            toolData.Quizes.find(qz => qz.quiz_item_id === mapped) ||
            null
          if (q) acc.push(q)
          return acc
        }, [])

        // Combine-formats dependencies ophalen (zoals voorheen)
        const additionalQuizIds = []
        filteredQuizes.forEach(quiz => {
          if (quiz.__component === "quiz-game-types.combine-formats") {
            quiz.stack.forEach(orderItem => {
              additionalQuizIds.push(orderItem.quiz_item_id)
            })
          }
        })

        const additionalQuizes = [
          ...toolData.Quizes.filter(q =>
            additionalQuizIds.includes(q.quiz_item_id)
          ),
          ...toolData.quiz_results.filter(r =>
            additionalQuizIds.includes(r.results_item_id)
          ),
        ]

        // Voeg extra quizes toe (behoud hoofdvolgorde, extras achteraan)
        additionalQuizes.forEach(q => {
          const id = q.quiz_item_id || q.results_item_id
          const already = filteredQuizes.find(
            existing => (existing.quiz_item_id || existing.results_item_id) === id
          )
          if (!already) filteredQuizes.push(q)
        })
      } else {
        // Geen filtering: alles meenemen
        filteredQuizes = [...toolData.Quizes]
      }

      // 3) Results volgens URL-volgorde
      if (ResultIds.length > 0) {
        filteredResults = toolData.quiz_results
          .filter(res => ResultIds.includes(res.results_item_id))
          .sort(
            (a, b) =>
              ResultIds.indexOf(a.results_item_id) -
              ResultIds.indexOf(b.results_item_id)
          )
      } else {
        filteredResults = toolData.quiz_results
      }

      // === Data toewijzen aan shape orders ===
      // let op: we voegen GEEN speciale "startscreen" injectie meer toe binnen shapes;
      // enkel normale matching. De StartScreen kan nu via de quiz-lijst in URL-volgorde komen.

      // quiz_shape_order_pre
      filteredShapes.forEach(shape => {
        if (shape.quiz_shape_order_pre && shape.quiz_shape_order_pre.length > 0) {
          shape.quiz_shape_order_pre.forEach(order => {
            const mappedId = mapQuizItemId(order.quiz_item_id)
            let datum =
              (mappedId === "startscreen_name" ? startScreenItem : null) ||
              toolData.Quizes.find(q => q.quiz_item_id === mappedId) ||
              toolData.quiz_results.find(r => r.results_item_id === mappedId) ||
              null
            if (datum) order.data = datum
            else console.error(`No matching quiz item found for quiz_item_id: ${order.quiz_item_id}`)
          })
        }
      })

      // quiz_shape_order
      filteredShapes.forEach(shape => {
        if (shape.quiz_shape_order && shape.quiz_shape_order.length > 0) {
          shape.quiz_shape_order.forEach(order => {
            const mappedId = mapQuizItemId(order.quiz_item_id)
            let datum =
              (mappedId === "startscreen_name" ? startScreenItem : null) ||
              toolData.Quizes.find(q => q.quiz_item_id === mappedId) ||
              toolData.quiz_results.find(r => r.results_item_id === mappedId) ||
              null
            if (datum) order.data = datum
            else console.error(`No matching quiz item found for quiz_item_id: ${order.quiz_item_id}`)
          })

          // originele orders bewaren
          shape.ori_quiz_shape_order_pre = shape.quiz_shape_order_pre
          shape.ori_quiz_shape_order = shape.quiz_shape_order
          shape.ori_quiz_shape_order_post = shape.quiz_shape_order_post

          // randomize (zoals voorheen)
          if (shape.randomize) {
            shape.quiz_shape_order = shuffleArray(shape.quiz_shape_order)
            if (shape.randomize_max_visible > 0) {
              shape.quiz_shape_order = shape.quiz_shape_order.slice(
                0,
                shape.randomize_max_visible
              )
            }
          }
        }
      })

      // quiz_shape_order_post
      filteredShapes.forEach(shape => {
        if (shape.quiz_shape_order_post && shape.quiz_shape_order_post.length > 0) {
          shape.quiz_shape_order_post.forEach(order => {
            const mappedId = mapQuizItemId(order.quiz_item_id)
            let datum =
              toolData.quiz_results.find(r => r.results_item_id === mappedId) ||
              toolData.Quizes.find(q => q.quiz_item_id === mappedId) ||
              (mappedId === "startscreen_name" ? startScreenItem : null) ||
              null
            if (datum) order.data = datum
            else console.error(`No matching quiz item found for quiz_item_id: ${order.quiz_item_id}`)
          })
        }
      })

      // pre + main + post combineren
      filteredShapes.forEach(shape => {
        const preOrder = shape.quiz_shape_order_pre || []
        const mainOrder = shape.quiz_shape_order || []
        const postOrder = shape.quiz_shape_order_post || []
        shape.quiz_shape_order_combined = [...preOrder, ...mainOrder, ...postOrder]
        delete shape.quiz_shape_order_pre
        delete shape.quiz_shape_order
        delete shape.quiz_shape_order_post
      })

      // combine-formats stack data toewijzen
      filteredQuizes.forEach(quizItem => {
        if (quizItem.__component === "quiz-game-types.combine-formats") {
          quizItem.stack.forEach(orderItem => {
            const mappedId = mapQuizItemId(orderItem.quiz_item_id)
            const datum = orderItem.question_or_result === "result"
              ? toolData.quiz_results.find(r => r.results_item_id === mappedId)
              : (mappedId === "startscreen_name"
                  ? startScreenItem
                  : toolData.Quizes.find(q => q.quiz_item_id === mappedId))
            if (datum) orderItem.data = datum
            else console.error(`No matching quiz item found for combine-formats: ${orderItem.quiz_item_id}`)
          })
        }
      })

      // results.component_order data toewijzen
      filteredResults.forEach(resultItem => {
        if (Array.isArray(resultItem.component_order)) {
          resultItem.component_order.forEach(orderItem => {
            const mappedId = mapQuizItemId(orderItem.quiz_item_id)
            const datum =
              toolData.Quizes.find(q => q.quiz_item_id === mappedId) ||
              (mappedId === "startscreen_name" ? startScreenItem : null)
            if (datum) orderItem.data = datum
            else console.error(`No matching quiz item found for component_order: ${orderItem.quiz_item_id}`)
          })
        }
      })

      // verifiëren
      filteredShapes.forEach(shape => {
        shape.quiz_shape_order_combined.forEach(orderItem => {
          if (!orderItem.data) {
            console.error(`Data not assigned for quiz_shape_order_combined item: ${orderItem.quiz_item_id}`)
          }
        })
      })

      // combine-formats niet dubbel renderen
      filteredQuizes = filteredQuizes.filter(
        q => q.__component !== "quiz-game-types.combine-formats"
      )

      // console.log("Filtered Quizes:", filteredQuizes.map(q => q.quiz_item_id))
      // console.log("Filtered Results:", filteredResults.map(r => r.results_item_id))
      // console.log("Filtered Shapes:", filteredShapes.map(s => s.quiz_shape_id))

      setQuiz({
        ...toolData,
        Quizes: filteredQuizes,
        quiz_results: filteredResults,
        quiz_shape: filteredShapes,
      })
      setLoading(false)
    })
    .catch(error => {
      console.error("Error fetching data: ", error)
      setError(error)
      setLoading(false)
    })
}


  // Helper function to find quiz item by ID
  const findQuizItemById = (quizItemId, quizes) => {
    return quizes.find(quiz => quiz.quiz_item_id === quizItemId)
  }

  if (loading) {
    return <>
    {loading && (
      <LoadingOverlay id="loadingscreen">
        <Spinner themeColor="#e0e0e0" />
      </LoadingOverlay>
    )}
    </>
  }

  if (error) {
    return <div>Error: {error.message}</div>
  }

  return (
    <>
      <QuizOverview
        quiz={quiz}
        oriQuiz={oriQuiz}
        loading={loading}
        error={error}
      />
    </>
  )
}

export default QuizGame
